import { useEffect, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import SearchBar from "../components/SearchBar";
import Colors from '../styles/Colors.json';
import './AdminEmployeeView.css';
import EmployeeViewManager from "../managers/EmployeeViewManager";
import { AdminEmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import { useSiteContext } from "../SiteContext";
import ToggleSwitch from "../components/ToggleSwitch";

const AdminEmployeeView: React.FC = () => {
    // State for search term input
    const [searchTerm, setSearchTerm] = useState<string>("");
    // State for selected supervisor filter
    const [selectedSort, setSelectedSort] = useState<string>("ALL");
    // State for storing raw employee data
    const [data, setData] = useState<AdminEmployeeInfo[] | null>(null);
    // State for storing sorted and filtered employee data
    const [sortedData, setSortedData] = useState<AdminEmployeeInfo[] | null>(null);
    // State for active/inactive employee filter
    const [isActive, setIsActive] = useState<boolean>(true);
    // State for pagination: current page
    const [currentPage, setCurrentPage] = useState<number>(1);
    // State for pagination: number of items per page
    const [itemsPerPage, setItemsPerPage] = useState<number>(30);

    // Context for managing loading state
    const { setLoading } = useSiteContext();

    // Fetch employee data on component mount
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await EmployeeViewManager.getAdminEmployees();
                setData(response); // Set fetched data
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Sort and filter data when supervisor or active status changes
    useEffect(() => {
        sortData(selectedSort, isActive);
    }, [selectedSort, isActive, data, searchTerm]);

    // Function to sort and filter employee data
    const sortData = async (sortType: string, active: boolean) => {
        if (!data) return;
        setLoading(true);
        setSelectedSort(sortType);
    
        let newData = data;
    
        // Filter data based on active status
        newData = newData.filter((employee) => {
            const matchesActiveStatus = employee.active === active;
            return matchesActiveStatus;
        });
    
        // Sort filtered data by altEmployeeID or employeeID
        newData = newData.sort((a, b) => {
            let aAttribute;
            let bAttribute;
    
            switch(sortType) {
                case "ID":
                    aAttribute = a.altEmployeeID ?? a.adminEmployeeID?.toLocaleString() ?? a.employeeID?.toLocaleString() ?? '';
                    bAttribute = b.altEmployeeID ?? b.adminEmployeeID?.toLocaleString() ?? b.employeeID?.toLocaleString() ?? '';
                    return aAttribute.localeCompare(bAttribute);
                case "Name":
                    aAttribute = a.name.first + " " + a.name.last;
                    bAttribute = b.name.first + " " + b.name.last;
                    return aAttribute.localeCompare(bAttribute);
                case "Email":
                    aAttribute = a.email ?? '';
                    bAttribute = b.email ?? '';
                    return aAttribute.localeCompare(bAttribute);
                default:
                    aAttribute = a.altEmployeeID ?? a.adminEmployeeID?.toLocaleString() ?? a.employeeID?.toLocaleString() ?? '';
                    bAttribute = b.altEmployeeID ?? b.adminEmployeeID?.toLocaleString() ?? b.employeeID?.toLocaleString() ?? '';
                    return aAttribute.localeCompare(bAttribute);
            }
        });
    
        setSortedData(newData); // Set sorted and filtered data
        setCurrentPage(1); // Reset current page
        setLoading(false);
    };

    // Toggle active/inactive filter
    const handleSwitchToggle = () => {
        setIsActive((prev) => !prev);
    };

    // Filter employees based on search term
    const filteredEmployees = sortedData?.filter((employee) => {
        if (searchTerm.trim() === "") {
            return true; // Return all employees if search term is empty
        }
        const searchTermLower = searchTerm.toLowerCase();

        // Search by name (if search term contains letters)
        if (/[a-zA-Z]/.test(searchTerm)) {
            const fullName = `${employee.name.first} ${employee.name.last}`.toLowerCase();
            return fullName.includes(searchTermLower);
        }

        // Search by employee ID or phone number (if search term is numeric)
        if (/^\d+$/.test(searchTerm)) {
            const empID = employee.altEmployeeID ? 
                employee.altEmployeeID.toString().includes(searchTerm) :
                employee.adminEmployeeID.toString().includes(searchTerm);
            const emailMatch = employee.email ? 
                employee.email.includes(searchTerm) : 
                false;
            return empID || emailMatch;
        }

        return false; // Exclude if no match
    }) || [];

    // Pagination logic: calculate indices for current page
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

    // Function to change page
    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    // Calculate total pages for pagination
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const maxVisibleButtons = 10; // Maximum number of visible pagination buttons

    // Function to render pagination buttons
    const renderPaginationButtons = () => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages); // Ensure current page is within valid range
        }
        const buttons = [];
        let startPage, endPage;

        // Determine the range of visible page buttons
        if (totalPages <= maxVisibleButtons) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const halfVisible = Math.floor(maxVisibleButtons / 2);
            if (currentPage <= halfVisible) {
                startPage = 1;
                endPage = maxVisibleButtons;
            } else if (currentPage + halfVisible >= totalPages) {
                startPage = totalPages - maxVisibleButtons + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - halfVisible;
                endPage = currentPage + halfVisible;
            }
        }

        // Add "Previous" button
        buttons.push(
            <button
                key="prev"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || totalPages <= 1}
            >
                Previous
            </button>
        );

        // Add "..." for overflow on the left
        if (startPage > 1) {
            buttons.push(
                <button key="start-ellipsis" disabled>
                    ...
                </button>
            );
        }

        // Add page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => paginate(i)}
                    className={currentPage === i ? "active" : ""}
                >
                    {i}
                </button>
            );
        }

        // Add "..." for overflow on the right
        if (endPage < totalPages) {
            buttons.push(
                <button key="end-ellipsis" disabled>
                    ...
                </button>
            );
        }

        // Add "Next" button
        buttons.push(
            <button
                key="next"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages <= 1}
            >
                Next
            </button>
        );

        return buttons;
    };

    // Function to format phone numbers
    function formatPhoneNumber(phoneNumber: string): string {
        const countryCode = phoneNumber.slice(0, 2);
        const areaCode = phoneNumber.slice(2, 5);
        const firstPart = phoneNumber.slice(5, 8);
        const secondPart = phoneNumber.slice(8, 12);
        return `${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    }

    return (
        <PageWrapper title="Admin Employee View" backgroundColor={Colors.White}>
            <div className="admin-view-top-row">
                <div className="admin-view-sort-by">
                    <label>Sort By: </label>
                    <select value={selectedSort} onChange={(e) => setSelectedSort(e.target.value)}>
                        <option key={0} value={"ID"}>
                            ID
                        </option>
                        <option key={1} value={"Name"}>
                            Name
                        </option>
                        <option key={2} value={"Email"}>
                            Email
                        </option>
                    </select>
                </div>
                <ToggleSwitch
                    label="Active:"
                    checked={isActive}
                    onChange={handleSwitchToggle}
                />
                <SearchBar
                    placeholder={"Search Admin Employees"}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    style={{ 
                        width: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'visible'
                    }}
                />
            </div>
            <div className="admin-view-table">
                <table>
                    <thead>
                        <tr className="admin-view-table-row-blue">
                            <th>First</th>
                            <th>Middle</th>
                            <th>Last</th>
                            <th>ID</th>
                            <th>Email</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((employee, index) => (
                            <tr key={index} className={index % 2 == 1 ? "admin-view-table-row-blue" : "admin-view-table-row-white"}>
                                <td>{employee.name.first}</td>
                                <td>{employee.name.middle ?? '-'}</td>
                                <td>{employee.name.last}</td>
                                <td>{employee.altEmployeeID ?? '-'}</td>
                                <td>{employee.email}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="admin-pagination">
                {renderPaginationButtons()}
            </div>
        </PageWrapper>
    );
};

export default AdminEmployeeView;